import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, FolderOpen, Sparkles, CheckCircle2, AlertTriangle, Radio, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { FASI, FASE_LABEL, FASE_STYLE, formatDate } from "../lib/constants";
import { ContentList } from "../components/ContentList";
import { Tilt3D } from "../components/Tilt3D";
import { FloatingShapes } from "../components/FloatingShapes";
import { ModuleOverview } from "../components/ModuleOverview";
import { FollowUpCard } from "../components/FollowUpCard";

const FollowUpDashboard = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/followups", { params: { stato: "aperto", limit: 4 } }).then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);
  if (!data?.items?.length) return null;
  return (
    <section data-testid="dashboard-followups" className="fade-up relative z-10">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg"><Mail className="h-5 w-5 text-primary" /> Follow-up da fare <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-white">{data.aperti}</span></h2>
        <Link to="/followup" data-testid="dashboard-followups-link" className="text-xs text-muted-foreground hover:text-white">Vedi tutti →</Link>
      </div>
      <ul className="mt-4 space-y-3">{data.items.map((it) => <FollowUpCard key={it.id} item={it} onChange={load} />)}</ul>
    </section>
  );
};

const StatCard = ({ label, value, icon: Icon, accent, testId }) => (
  <Tilt3D data-testid={testId} className="surface stat-3d p-5">
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <Icon className="h-4 w-4" style={{ color: accent }} />
    </div>
    <p className="mt-3 text-3xl font-bold tracking-tight font-mono">{value}</p>
  </Tilt3D>
);

export default function Dashboard() {
  const { user } = useAuth();
  const f = user?.features || {};
  const sales = f.sales_help !== false;
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [overview, setOverview] = useState(null);

  const load = useCallback(async () => {
    try {
      const reqs = [api.get("/overview")];
      if (sales) reqs.push(api.get("/stats"), api.get("/contents", { params: { page_size: 8 } }));
      const [o, s, r] = await Promise.all(reqs);
      setOverview(o.data);
      if (s) setStats(s.data);
      if (r) setRecent(r.data.items);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [sales]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const inCorso = stats ? stats.per_stato.CARICATO + stats.per_stato.IN_TRASCRIZIONE + stats.per_stato.IN_ANALISI : 0;
  const perFase = stats?.per_fase || {};
  const maxCat = stats ? Math.max(1, ...Object.values(perFase)) : 1;

  return (
    <div data-testid="dashboard-page" className="relative space-y-10">
      <FloatingShapes />
      <div className="fade-up relative z-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Pannello di controllo</p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Dashboard</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {sales ? <>Ultimo aggiornamento: <span className="font-mono">{formatDate(stats?.ultimo_aggiornamento)}</span></> : "Riepilogo dei moduli attivi sul tuo account."}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {sales && (
            <Link to="/live" data-testid="dashboard-live-btn" className="inline-flex items-center gap-2 self-start rounded-full border border-[#F5B942]/50 bg-[#F5B942]/10 px-5 py-3 text-sm font-semibold text-[#FCD990] transition-all hover:-translate-y-0.5 hover:bg-[#F5B942]/20">
              <Radio className="h-4 w-4" /> Assistente Live
            </Link>
          )}
          {sales ? (
            <Link to="/aggiungi" data-testid="dashboard-add-content-btn" className="inline-flex items-center gap-2 self-start rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white shadow-[0_0_24px_rgba(37,99,235,0.35)] transition-all hover:-translate-y-0.5 hover:bg-[#1D4ED8]">
              <Plus className="h-4 w-4" /> Aggiungi contenuto
            </Link>
          ) : f.chiamate !== false && (
            <Link to="/chiamate" data-testid="dashboard-calls-btn" className="inline-flex items-center gap-2 self-start rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white shadow-[0_0_24px_rgba(37,99,235,0.35)] transition-all hover:-translate-y-0.5 hover:bg-[#1D4ED8]">
              <Phone className="h-4 w-4" /> Avvia una chiamata
            </Link>
          )}
        </div>
      </div>

      <ModuleOverview data={overview} />

      <FollowUpDashboard />

      {sales && (
        <>
          <section className="fade-up relative z-10">
            <h2 className="mb-4 text-base font-semibold md:text-lg">Sales Help · Knowledge Base</h2>
            <div className="stagger persp grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard testId="stat-contenuti" label="Contenuti caricati" value={stats?.contenuti_totali ?? "—"} icon={FolderOpen} accent="#60A5FA" />
              <StatCard testId="stat-conoscenze" label="Conoscenze estratte" value={stats?.conoscenze_totali ?? "—"} icon={Sparkles} accent="#F5B942" />
              <StatCard testId="stat-indicizzati" label="Indicizzati" value={stats?.per_stato.INDICIZZATO ?? "—"} icon={CheckCircle2} accent="#10B981" />
              <StatCard testId="stat-errori" label={inCorso ? "In elaborazione" : "Errori"} value={stats ? (inCorso || stats.per_stato.ERRORE) : "—"} icon={AlertTriangle} accent={inCorso ? "#F59E0B" : "#EF4444"} />
            </div>
          </section>

          <div className="relative z-10 grid gap-8 lg:grid-cols-5">
            <section className="fade-up min-w-0 lg:col-span-2">
              <h2 className="text-base font-semibold md:text-lg">Split per fase</h2>
              <div data-testid="category-split" className="surface mt-4 space-y-4 p-5">
                {FASI.map((fase) => {
                  const n = perFase[fase] ?? 0; const st = FASE_STYLE[fase];
                  return (
                    <div key={fase} data-testid={`category-split-row-${fase}`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium">{FASE_LABEL[fase]}</span>
                        <span className="font-mono text-muted-foreground">{n}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(n / maxCat) * 100}%`, background: st.bar }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="fade-up min-w-0 lg:col-span-3">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold md:text-lg">Contenuti recenti</h2>
                <Link to="/contenuti" data-testid="dashboard-view-all-link" className="text-xs text-muted-foreground hover:text-white">Vedi tutti →</Link>
              </div>
              <div className="mt-4">
                <ContentList items={recent} />
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
